import { useState, useMemo, useRef, useEffect } from "react";
import { Modal } from "../../../evo/bootstrap";
import { ArrowLeft, BadgeCheck, ChartLine, ChevronDown, CircleAlert, CircleCheck, CirclePlus, Eye, Info, Lightbulb, Loader2,
    Search, SquarePen, TriangleAlert, Wrench } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Field, ModalFooter, ModalHeader, NativeSelect, Required } from "@/components/admin/cards";
import { cn } from "@/lib/utils";
import { reviewImproveFaqs, get_All_qna, InsertMandetoryQue } from "../../Services/chatbot";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DateRangeSelector from "../DateRangeFilter/index";
import "../../assets/chatbotconfig.css";

const RESULT_TABS = [
    { key: "faq_performance", label: "Overview", title: "How well your bot is performing overall", icon: <ChartLine aria-hidden="true" /> },
    { key: "needs_faq", label: "Missing Answers", title: "Questions your bot can't answer yet", icon: <CircleAlert aria-hidden="true" /> },
    { key: "already_covered", label: "Improve Existing", title: "FAQ answers that need tuning", icon: <Wrench aria-hidden="true" /> },
    { key: "review", label: "Low Quality", title: "Answers that may be inaccurate — please review", icon: <Eye aria-hidden="true" /> },
];

const HINT_TONES = {
    warning: "bg-warning-500/15 text-warning-700",
    info: "bg-info-500/15 text-info-700",
    danger: "bg-destructive/10 text-destructive",
};

const Hint = ({ tone, icon, children }: { tone: keyof typeof HINT_TONES; icon: ReactNode; children: ReactNode }) => (
    <p className={cn("flex items-start gap-2 rounded-lg px-3 py-2 text-sm [&_svg]:mt-0.5 [&_svg]:size-4 [&_svg]:shrink-0", HINT_TONES[tone])}>
        {icon}
        <span>{children}</span>
    </p>
);

const ReviewImproveFaqsModal = ({ show, onHide, onFaqAdded, addedFaqQuestions = [], setIsNeedPublish, setPublishTrigger }: any) => {
    const getDefaultDateRange = () => {
        const today = new Date();
        const lastMonth = new Date(today);
        lastMonth.setDate(lastMonth.getDate() - 30);
        return { startDate: lastMonth, endDate: today, key: "selection" };
    };

    // State
    const [dateRange, setDateRange] = useState<any>([getDefaultDateRange()]);
    const [minFrequency, setMinFrequency] = useState(1);
    const [maxSuggestions, setMaxSuggestions] = useState(50);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState("form"); // "form" or "results"

    // Results
    const [needsFaq, setNeedsFaq] = useState<any>([]);
    const [alreadyCovered, setAlreadyCovered] = useState<any>([]);
    const [faqPerformance, setFaqPerformance] = useState<any>([]);
    const [review, setReview] = useState<any>([]);
    const [stats, setStats] = useState<any>(null);

    // Active tab & expanded row
    const [activeTab, setActiveTab] = useState("faq_performance");
    const [expandedRow, setExpandedRow] = useState<any>(null);
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());

    const QUESTION_MAX_LENGTH = 120;

    const toggleQuestionExpand = (index, e) => {
        e.stopPropagation();
        setExpandedQuestions(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // Inline FAQ edit state
    const [inlineEditIndex, setInlineEditIndex] = useState<any>(null);
    const [inlineOriginalQuestion, setInlineOriginalQuestion] = useState("");
    const [inlineQuestion, setInlineQuestion] = useState("");
    const [inlineAnswer, setInlineAnswer] = useState("");
    const [inlineSaving, setInlineSaving] = useState(false);
    const [localAddedQuestions, setLocalAddedQuestions] = useState<any>([]);
    const inlineFormRef = useRef<any>(null);

    useEffect(() => {
        if (inlineEditIndex !== null && inlineFormRef.current) {
            inlineFormRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [inlineEditIndex]);

    const [existingFaqQuestions, setExistingFaqQuestions] = useState<any>([]);

    const allAddedQuestions = useMemo(() => {
        const combined = [...addedFaqQuestions, ...localAddedQuestions, ...existingFaqQuestions];
        return new Set(combined.map(q => q.toLowerCase().trim()));
    }, [addedFaqQuestions, localAddedQuestions, existingFaqQuestions]);

    const isQuestionAdded = (question) => {
        return allAddedQuestions.has((question || "").toLowerCase().trim());
    };

    const stripHtml = (html) => {
        if (!html) return "";
        const tmp = document.createElement("div");
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || "";
    };

    const handleInlineEdit = (item, index) => {
        setInlineEditIndex(index);
        setInlineOriginalQuestion(item.question || "");
        setInlineQuestion(item.question || "");
        setInlineAnswer(stripHtml(item.sample_answer) || "");
    };

    const handleInlineCancel = () => {
        setInlineEditIndex(null);
        setInlineOriginalQuestion("");
        setInlineQuestion("");
        setInlineAnswer("");
    };

    const handleInlineSave = async () => {
        if (!inlineQuestion.trim()) {
            toast.error("Question is required");
            return;
        }
        if (!inlineAnswer.trim()) {
            toast.error("Answer is required");
            return;
        }
        setInlineSaving(true);
        try {
            const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, "");
            const uniqueIntentID = `Q${timestamp}`;
            const orderId = Math.floor(Math.random() * 10000).toString();
            const payload = {
                question: inlineQuestion,
                answer: inlineAnswer,
                intentID: uniqueIntentID,
                order_id: orderId,
                type: 'QnA',
                original_question: inlineOriginalQuestion
            };
            await InsertMandetoryQue(payload);
            toast.success("FAQ added successfully!");
            if (setIsNeedPublish) setIsNeedPublish(true);
            if (setPublishTrigger) setPublishTrigger(prev => prev + 1);
            const added = [inlineQuestion];
            if (inlineOriginalQuestion && inlineOriginalQuestion.toLowerCase().trim() !== inlineQuestion.toLowerCase().trim()) {
                added.push(inlineOriginalQuestion);
            }
            setLocalAddedQuestions(prev => [...prev, ...added]);
            if (onFaqAdded) {
                added.forEach(q => onFaqAdded(q));
            }
            setInlineEditIndex(null);
            setInlineOriginalQuestion("");
            setInlineQuestion("");
            setInlineAnswer("");
        } catch (error) {
            console.error("Failed to save FAQ:", error);
            toast.error(error?.message || "Failed to save FAQ");
        } finally {
            setInlineSaving(false);
        }
    };

    // API call
    const handleFindSuggestions = async () => {
        setLoading(true);
        try {
            const fromDate = dateRange[0]?.startDate
                ? format(dateRange[0].startDate, "yyyy-MM-dd")
                : null;
            const toDate = dateRange[0]?.endDate
                ? format(dateRange[0].endDate, "yyyy-MM-dd")
                : null;

            // Fetch existing FAQs to pass to backend for classification
            let existingFaqs = [];
            try {
                const qnaRes = await get_All_qna();
                if (qnaRes?.status === 200 && Array.isArray(qnaRes.data)) {
                    existingFaqs = qnaRes.data.flatMap(item => {
                        const questions = [item.question].filter(q => q && q.trim());
                        if (item.original_question && item.original_question.trim()) {
                            questions.push(item.original_question);
                        }
                        return questions;
                    });
                    setExistingFaqQuestions(existingFaqs);
                }
            } catch (e) {
                console.warn("Could not fetch existing FAQs, continuing without them", e);
            }

            const res = await reviewImproveFaqs({
                from_date: fromDate,
                to_date: toDate,
                min_frequency: minFrequency,
                max_suggestions: maxSuggestions,
                existing_faqs: existingFaqs,
            });

            if (!res || !res.data) {
                throw new Error("Invalid API response");
            }

            setNeedsFaq(res.data.needs_faq || []);
            setAlreadyCovered(res.data.already_covered || []);
            setFaqPerformance(res.data.faq_performance || []);
            setReview(res.data.review || []);
            setStats(res.data.stats || null);
            setActiveTab("faq_performance");
            setExpandedRow(null);
            setView("results");
        } catch (error) {
            console.error("Failed to fetch FAQ analysis", error);
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch FAQ analysis";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setView("form");
        setExpandedRow(null);
        setInlineEditIndex(null);
    };

    const handleClose = () => {
        setDateRange([getDefaultDateRange()]);
        setMinFrequency(1);
        setMaxSuggestions(50);
        setNeedsFaq([]);
        setAlreadyCovered([]);
        setFaqPerformance([]);
        setReview([]);
        setStats(null);
        setView("form");
        setExpandedRow(null);
        setInlineEditIndex(null);
        setInlineOriginalQuestion("");
        setInlineQuestion("");
        setInlineAnswer("");
        onHide();
    };

    // Active tab data
    const activeData = useMemo(() => {
        if (activeTab === "needs_faq") return needsFaq;
        if (activeTab === "already_covered") return alreadyCovered;
        if (activeTab === "review") return review;
        return faqPerformance;
    }, [activeTab, needsFaq, alreadyCovered, faqPerformance, review]);

    const tabCounts = {
        needs_faq: needsFaq.length,
        already_covered: alreadyCovered.length,
        faq_performance: faqPerformance.length,
        review: review.length,
    };

    // Per-tab configuration for hotelier-friendly presentation
    const tabConfig = {
        faq_performance: {
            description: "A complete view of every question guests have asked your bot.",
            emptyMessage: "No questions found for the selected date range.",
            actionLabel: "Add to FAQ",
            columns: { col2: "Answered", col3: "Unanswered" },
        },
        needs_faq: {
            description: "These questions have no matching FAQ — guests are not getting helpful answers.",
            emptyMessage: "Great news! Your bot has answers for all common questions.",
            actionLabel: "Add to FAQ",
            columns: { col2: "Answered", col3: "Unanswered" },
        },
        already_covered: {
            description: "Your FAQ covers these topics, but the answers may need improvement.",
            emptyMessage: "All your existing FAQ answers are performing well.",
            actionLabel: "Improve Answer",
            columns: { col2: "Answered", col3: "Unanswered" },
        },
        review: {
            description: "These answers may be inaccurate or unhelpful — please review them.",
            emptyMessage: "No low-quality answers detected. Your bot is doing well!",
            actionLabel: "Review & Fix",
            columns: { col2: "Answered", col3: "Unanswered" },
        },
    };

    const currentTabConfig = tabConfig[activeTab] || tabConfig.faq_performance;

    // Status badge matching screenshot style
    const getStatusBadge = (status) => {
        const tone = status === "Working Well" ? "bg-success-500/15 text-success-700"
            : status === "Needs Attention" ? "bg-warning-500/15 text-warning-700"
                : "bg-destructive/10 text-destructive";
        const label = status === "Working Well" || status === "Needs Attention" ? status : "Not Working";
        return <span className={cn("rounded-full px-2 py-0.5 font-medium", tone)}>{label}</span>;
    };

    const getAnswerSourceLabel = (source) => {
        if (source === "vector_data") return "Your FAQs";
        if (source === "generic_llm") return "General AI Knowledge";
        if (source === "other" || source === "unknown") return "Unknown";
        return source || "Unknown";
    };

    const toggleRow = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
        setInlineEditIndex(null);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            size="lg"
            dialogClassName="review-improve-faqs-modal"
            backdrop="static"
            keyboard={false}
            enforceFocus={false}
            centered
        >
            <ModalHeader
                icon={<Lightbulb />}
                title="Review & Improve FAQs"
                description="Find guest questions your chatbot struggles with and turn them into FAQs."
                onClose={handleClose}
            />

            <Modal.Body className="px-4 py-3">
                {/* FORM VIEW */}
                {view === "form" && (
                    <>
                        <div className="row align-items-end row-gap-3">
                            <div className="col-md-5">
                                <DateRangeSelector
                                    value={dateRange}
                                    small={true}
                                    onSelect={({ from_date, to_date }) => {
                                        if (!from_date || !to_date) {
                                            setDateRange([getDefaultDateRange()]);
                                        } else {
                                            setDateRange([{
                                                startDate: new Date(from_date),
                                                endDate: new Date(to_date),
                                                key: "selection",
                                            }]);
                                        }
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <div data-evo-island>
                                    <Field label="Min Frequency" htmlFor="reviewFaqsMinFrequency">
                                        <Input
                                            id="reviewFaqsMinFrequency"
                                            type="number"
                                            min="1"
                                            value={minFrequency}
                                            onChange={(e) =>
                                                setMinFrequency(Math.max(1, parseInt(e.target.value) || 1))
                                            }
                                        />
                                    </Field>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div data-evo-island>
                                    <Field label="Max Records" htmlFor="reviewFaqsMaxRecords">
                                        <NativeSelect
                                            id="reviewFaqsMaxRecords"
                                            value={maxSuggestions}
                                            onChange={(e) => setMaxSuggestions(parseInt(e.target.value))}
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                            <option value={500}>500</option>
                                        </NativeSelect>
                                    </Field>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div data-evo-island className="flex justify-end">
                                    <Button type="button" onClick={handleFindSuggestions} disabled={loading} className="max-md:w-full">
                                        {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Search aria-hidden="true" />}
                                        {loading ? "Analyzing..." : "Find Suggestions"}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div data-evo-island className="mt-4">
                            {loading ? (
                                <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground" role="status">
                                    <Loader2 className="size-5 animate-spin text-primary" aria-hidden="true" />
                                    Analyzing chat history...
                                </div>
                            ) : (
                                <EmptyState
                                    icon={<Lightbulb />}
                                    title="Ready when you are"
                                    description="Select a date range and click Find Suggestions to analyze your chat history."
                                />
                            )}
                        </div>
                    </>
                )}

                {/* RESULTS VIEW */}
                {view === "results" && (
                    <div data-evo-island className="flex flex-col gap-3 text-foreground">
                        <div>
                            <h3 className="text-base font-semibold">FAQ Analysis Results</h3>
                            <p className="text-sm text-muted-foreground">
                                Analyzed {stats?.total_qa_pairs_found || stats?.total_conversations_analyzed || 0} questions across {stats?.sessions_checked || stats?.total_conversations_analyzed || 0} conversations
                                {stats?.total_unique_questions > 0 && (
                                    <> &middot; {stats.total_unique_questions} unique questions found</>
                                )}
                            </p>
                        </div>

                        {/* Analytics sub-tabs — hotelier-friendly labels */}
                        <div role="tablist" aria-label="Analysis results" className="flex flex-wrap gap-1 rounded-lg bg-muted p-1">
                            {RESULT_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === tab.key}
                                    title={tab.title}
                                    onClick={() => { setActiveTab(tab.key); setExpandedRow(null); setInlineEditIndex(null); }}
                                    className={cn(
                                        "inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground [&_svg]:size-4",
                                        activeTab === tab.key && "bg-background text-foreground shadow-sm"
                                    )}
                                >
                                    {tab.icon}
                                    {tab.label} ({tabCounts[tab.key]})
                                </button>
                            ))}
                        </div>

                        <p className="text-sm text-muted-foreground">{currentTabConfig.description}</p>

                        {activeData.length >= maxSuggestions && (
                            <div className="flex items-center gap-2 rounded-lg bg-warning-500/15 px-3 py-2 text-sm text-warning-700">
                                <Info className="size-4 shrink-0" aria-hidden="true" />
                                Showing top {maxSuggestions} results. Increase &quot;Max Records&quot; to see more.
                            </div>
                        )}

                        {activeData.length === 0 ? (
                            <EmptyState icon={<CircleCheck />} title="Nothing to fix here" description={currentTabConfig.emptyMessage} />
                        ) : (
                            <ul className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto pe-1">
                                {activeData.map((item, index) => (
                                    <li key={index} className={cn("rounded-xl border border-border bg-card", expandedRow === index && "ring-1 ring-primary/30")}>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            aria-expanded={expandedRow === index}
                                            className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-accent/50"
                                            onClick={() => toggleRow(index)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRow(index); }
                                            }}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium break-words">
                                                    {item.question && item.question.length > QUESTION_MAX_LENGTH && !expandedQuestions.has(index) ? (
                                                        <>
                                                            {item.question.slice(0, QUESTION_MAX_LENGTH)}...
                                                            <span className="review-faqs-see-more ms-1 cursor-pointer text-primary hover:underline" onClick={(e) => toggleQuestionExpand(index, e)}>
                                                                See more
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {item.question}
                                                            {item.question && item.question.length > QUESTION_MAX_LENGTH && (
                                                                <span className="review-faqs-see-more ms-1 cursor-pointer text-primary hover:underline" onClick={(e) => toggleQuestionExpand(index, e)}>
                                                                    See less
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </p>
                                                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                                                    <span className="text-muted-foreground">Asked {item.frequency} times</span>
                                                    <span className="rounded-full bg-success-500/15 px-2 py-0.5 font-medium text-success-700">
                                                        {currentTabConfig.columns.col2}: {item.success_count || 0}
                                                    </span>
                                                    <span className="rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive">
                                                        {currentTabConfig.columns.col3}: {item.failed_count || 0}
                                                    </span>
                                                    {getStatusBadge(item.status)}
                                                    {isQuestionAdded(item.question) && (
                                                        <span className="review-faqs-added-badge inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
                                                            <BadgeCheck className="size-3" aria-hidden="true" />FAQ Added
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <ChevronDown className={cn("mt-1 size-4 shrink-0 text-muted-foreground transition-transform", expandedRow === index && "rotate-180")} aria-hidden="true" />
                                        </div>

                                        {expandedRow === index && (
                                            <div className="flex flex-col gap-3 border-t border-border bg-muted/40 px-3 py-3">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <h4 className="text-sm font-semibold">Bot Response Detail</h4>
                                                    {isQuestionAdded(item.question) ? (
                                                        <span className="review-faqs-added-badge-detail inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                            <BadgeCheck className="size-3" aria-hidden="true" />FAQ Added
                                                        </span>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleInlineEdit(item, index);
                                                            }}
                                                            disabled={inlineEditIndex === index}
                                                        >
                                                            <CirclePlus aria-hidden="true" />
                                                            {currentTabConfig.actionLabel}
                                                        </Button>
                                                    )}
                                                </div>

                                                {item.sample_answer && (
                                                    <div>
                                                        <p className="mb-1 text-xs font-medium text-muted-foreground">Sample Bot Answer:</p>
                                                        <div className="rounded-2xl rounded-tl-sm bg-background px-3 py-2 text-sm">{item.sample_answer}</div>
                                                    </div>
                                                )}

                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                                    {item.dominant_confidence && (
                                                        <span><span className="text-muted-foreground">Bot Confidence: </span><span className="font-semibold capitalize">{item.dominant_confidence}</span></span>
                                                    )}
                                                    {item.success_rate !== undefined && (
                                                        <span><span className="text-muted-foreground">Answer Rate: </span><span className="font-semibold">{Math.round((item.success_rate || 0) * 100)}%</span></span>
                                                    )}
                                                    {item.avg_similarity !== undefined && item.avg_similarity !== null && (
                                                        <span><span className="text-muted-foreground">FAQ Match: </span><span className="font-semibold">{Math.round((item.avg_similarity || 0) * 100)}%</span></span>
                                                    )}
                                                    {item.avg_response_time_ms !== undefined && item.avg_response_time_ms !== null && (
                                                        <span><span className="text-muted-foreground">Response Time: </span><span className="font-semibold">{Math.round(item.avg_response_time_ms || 0)}ms</span></span>
                                                    )}
                                                    {item.answer_source && (
                                                        <span><span className="text-muted-foreground">Answer Source: </span><span className="font-semibold">{getAnswerSourceLabel(item.answer_source)}</span></span>
                                                    )}
                                                </div>

                                                {activeTab === "needs_faq" && (
                                                    <Hint tone="warning" icon={<Lightbulb />}>
                                                        Your bot doesn&apos;t have an answer for this. Add it as a FAQ so guests get a helpful response.
                                                    </Hint>
                                                )}
                                                {activeTab === "already_covered" && (
                                                    <Hint tone="info" icon={<Wrench />}>
                                                        This topic is in your FAQ but guests aren&apos;t getting good answers. Consider updating the FAQ entry.
                                                    </Hint>
                                                )}
                                                {activeTab === "review" && (
                                                    <Hint tone="danger" icon={<TriangleAlert />}>
                                                        This answer may be inaccurate. Please review and correct it to avoid misleading guests.
                                                    </Hint>
                                                )}
                                                {activeTab === "faq_performance" && (item.failed_count > 0 || item.status === "Not Working") && (
                                                    <Hint tone="danger" icon={<TriangleAlert />}>
                                                        This question has unanswered instances. Consider adding or improving the FAQ entry.
                                                    </Hint>
                                                )}

                                                {/* Inline FAQ Edit Form */}
                                                {inlineEditIndex === index && !isQuestionAdded(item.question) && (
                                                    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3" ref={inlineFormRef}>
                                                        <h4 className="flex items-center gap-1.5 text-sm font-semibold">
                                                            <SquarePen className="size-4 text-primary" aria-hidden="true" />
                                                            {activeTab === "already_covered" || activeTab === "review" ? "Edit FAQ Answer" : "Add New FAQ"}
                                                        </h4>
                                                        <Field label={<>Question<Required /></>} htmlFor={`reviewFaqQuestion${index}`}>
                                                            <Input
                                                                id={`reviewFaqQuestion${index}`}
                                                                value={inlineQuestion}
                                                                onChange={(e) => setInlineQuestion(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </Field>
                                                        <Field label={<>Answer<Required /></>} htmlFor={`reviewFaqAnswer${index}`}>
                                                            <Textarea
                                                                id={`reviewFaqAnswer${index}`}
                                                                rows={4}
                                                                value={inlineAnswer}
                                                                onChange={(e) => setInlineAnswer(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                placeholder="Type the FAQ answer here..."
                                                            />
                                                        </Field>
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); handleInlineCancel(); }}
                                                                disabled={inlineSaving}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                onClick={(e) => { e.stopPropagation(); handleInlineSave(); }}
                                                                disabled={inlineSaving}
                                                            >
                                                                {inlineSaving && <Loader2 className="animate-spin" aria-hidden="true" />}
                                                                {inlineSaving ? "Saving..." : "Save FAQ"}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </Modal.Body>

            <ModalFooter>
                {view === "results" && (
                    <Button type="button" variant="outline" onClick={handleBack} className="me-auto">
                        <ArrowLeft aria-hidden="true" />
                        Back
                    </Button>
                )}
                <Button type="button" variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ReviewImproveFaqsModal;
